import type { ReactElement } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { Navigate } from 'react-router-dom';
import { useGetMyProfileQuery } from '../../../services/ProfileService';
import { hasInsuranceVerifyAccess } from '../../profile';

/**
 * Route-level authorization guard for the Financial Clearance Pre-Quote routes.
 *
 * Mirrors `modules/claims/routes/RequireCamEdit.tsx`. Previously the **InsuranceVerify** claim
 * check existed only in the nav menu (`FinancialClearanceMenu`) — hiding a menu item is not
 * access control, so any authenticated user could reach `/financial-clearance/pre-quotes` by
 * typing the URL. This guard mounts `children` ONLY once the profile has resolved AND the user
 * has the claim, so an unauthorized user's page shell/data hooks never mount.
 */
export function RequireInsuranceVerifyAccess({ children }: { children: ReactElement }): ReactElement | null {
  const { data: profile, isLoading } = useGetMyProfileQuery();

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress data-testid="financial-clearance-auth-loading" />
      </Box>
    );
  }

  if (!hasInsuranceVerifyAccess(profile))
    return <Navigate to="/" replace />;

  return children;
}
